import type {
  ReceivableUploadResponse,
  ReceivableUploadStatus
} from "services/api/offPlatformReceivables";

export const POLL_INTERVAL_MS = 2500;
export const POLL_MAX_DURATION_MS = 10 * 60 * 1000;
export const POLL_MAX_CONSECUTIVE_FAILURES = 3;

export type PollUploadOutcome =
  | { kind: "terminal"; upload: ReceivableUploadResponse }
  | { kind: "timedOut" }
  | { kind: "cancelled" };

export interface PollUploadOptions {
  fetchUpload: (uploadId: string) => Promise<ReceivableUploadResponse>;
  onProgress?: (upload: ReceivableUploadResponse) => void;
  intervalMs?: number;
  maxDurationMs?: number;
  maxConsecutiveFailures?: number;
}

export const isTerminalUploadStatus = (status: ReceivableUploadStatus) =>
  status === "completed" || status === "errored";

export default function pollUploadStatus(
  uploadId: string,
  {
    fetchUpload,
    onProgress,
    intervalMs = POLL_INTERVAL_MS,
    maxDurationMs = POLL_MAX_DURATION_MS,
    maxConsecutiveFailures = POLL_MAX_CONSECUTIVE_FAILURES
  }: PollUploadOptions
) {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let settled = false;
  let consecutiveFailures = 0;
  // A wall-clock deadline rather than a count of intervals: fetch latency is real elapsed time, and
  // summing intervals alone would let a 10 minute cap run well past 10 minutes.
  const deadline = Date.now() + maxDurationMs;
  let resolvePoll: (outcome: PollUploadOutcome) => void;
  let rejectPoll: (error: unknown) => void;

  const promise = new Promise<PollUploadOutcome>((resolve, reject) => {
    resolvePoll = resolve;
    rejectPoll = reject;
  });

  const stopTimer = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const settle = (outcome: PollUploadOutcome) => {
    if (settled) {
      return;
    }
    settled = true;
    stopTimer();
    resolvePoll(outcome);
  };

  const fail = (error: unknown) => {
    if (settled) {
      return;
    }
    settled = true;
    stopTimer();
    rejectPoll(error);
  };

  // Kept off the fetch path entirely: a consumer render error must neither be counted as a fetch
  // failure nor escape this fire-and-forget tick as an unhandled rejection.
  const notifyProgress = (upload: ReceivableUploadResponse) => {
    if (!onProgress) {
      return;
    }
    try {
      onProgress(upload);
    } catch {
      // ignored on purpose — polling owns the upload lifecycle, the callback only observes it
    }
  };

  const scheduleNext = () => {
    if (settled) {
      return;
    }
    if (Date.now() + intervalMs > deadline) {
      settle({ kind: "timedOut" });
      return;
    }
    timeoutId = setTimeout(() => {
      timeoutId = null;
      tick();
    }, intervalMs);
  };

  const tick = async () => {
    if (settled) {
      return;
    }

    let upload: ReceivableUploadResponse;
    try {
      upload = await fetchUpload(uploadId);
    } catch (error) {
      if (settled) {
        return;
      }
      consecutiveFailures += 1;
      if (consecutiveFailures >= maxConsecutiveFailures) {
        fail(error);
        return;
      }
      scheduleNext();
      return;
    }

    if (settled) {
      return;
    }
    consecutiveFailures = 0;

    // Settle before notifying, so a finished upload can never be left polling on to its timeout.
    if (isTerminalUploadStatus(upload.status)) {
      settle({ kind: "terminal", upload });
      notifyProgress(upload);
      return;
    }
    scheduleNext();
    notifyProgress(upload);
  };

  tick();

  return {
    promise,
    cancel: () => settle({ kind: "cancelled" })
  };
}
